import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import { OccupationField } from '@interlayer/js/app/components/main/Registration/components/fields/OccupationField/OccupationField';

import { Button } from 'js/components/common/Button';
import ErrorMessage from 'js/components/common/Fields/ErrorMessage';
import Form from 'js/components/common/Form';
import { I18n } from 'js/components/common/I18n';

import { setPlayerTags } from '@core/js/hooks/user/server';

import { changeDetails } from '@interlayer/js/app/components/main/Registration/server';
import {
    useGetOccupationTags
} from '@interlayer/js/app/components/popups/OccupationPopup/components/OccupationForm/hooks/useGetOccupationTags';
import { useIsOtherOccupation } from '@interlayer/js/app/components/popups/OccupationPopup/hooks/useIsOtherOccupation';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { useAllPlayerTags } from '@interlayer/js/app/hooks/user/hooks/useAllPlayerTags';
import { useGetUrlTemplatesForSegmentation } from '@interlayer/js/app/hooks/user/hooks/useGetUrlTemplatesForSegmentation';
import { useValidationRules } from '@interlayer/js/app/hooks/useValidationRules';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { setIsShowOccupationPopup, setOccupation } from '@interlayer/js/hooks/user/store/buzz/actions';

import { useTranslations } from 'js/hooks/i18n';
import { useFetchUserDetails } from 'js/hooks/user';
import { useUserSelector } from 'js/store/hooks';

import style from './OccupationForm.scss';

export const OccupationForm = () => {
    const dispatch = useDispatch();
    const fetchUserDetails = useFetchUserDetails();
    const getOccupationTags = useGetOccupationTags();
    const { isOther, setIsOther, validationFields } = useIsOtherOccupation();
    const validationConfig = useValidationRules(validationFields, 'occupationForm', {
        withOccupationExternal: true,
        isOccupationMandatoryExternal: true
    });
    const { fetch: fetchAllPlayerTags } = useAllPlayerTags({ isInitFetch: false });
    const allPlayerTags = useUserSelector(prop('allPlayerTags'));
    const translate = useTranslations('playtech.mobile.system.occupationPopup.');
    const getUrlTemplatesForSegmentation = useGetUrlTemplatesForSegmentation();
    const [
        dTagOccupationForcePlayerTag = 'Athena/Occupation=Force',
        dTagOccupationProvidedPlayerTag = 'Athena/Occupation=Provided'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.occupationForcePlayerTag,
        DYNAMIC_TAG_NAMES.occupationProvidedPlayerTag
    ]);
    const isForce = useMemo(() => {
        const [name, value] = dTagOccupationForcePlayerTag.split('=');

        return allPlayerTags?.[name]?.value === value;
    }, [allPlayerTags, dTagOccupationForcePlayerTag]);

    const onSubmit = useCallback(({ occupation }) => {
        dispatch(setOccupation(true));

        return changeDetails({ dataMap: { occupation } })
            .then(fetchUserDetails)
            .then(() => {
                const [name, value] = dTagOccupationProvidedPlayerTag.split('=');

                setPlayerTags([
                    ...getOccupationTags(occupation),
                    {
                        name,
                        ...value && {
                            value,
                            type: 'text'
                        }
                    }
                ])
                    .then(() => {
                        fetchAllPlayerTags({ isUpdate: true });
                        getUrlTemplatesForSegmentation();
                    });
            });
    }, [
        dispatch,
        fetchUserDetails,
        getOccupationTags,
        fetchAllPlayerTags,
        getUrlTemplatesForSegmentation,
        dTagOccupationProvidedPlayerTag
    ]);

    const handleCloseClick = () => {
        dispatch(setIsShowOccupationPopup(false));
    };

    return (
        <Form
            onSubmit={onSubmit}
            validationConfig={validationConfig}
            render={({ submitting, hasValidationErrors, submitError, handleSubmit }) => (
                <form
                    className={style.form}
                    onSubmit={handleSubmit}
                >
                    {submitError && (
                        <ErrorMessage error={submitError} animate isFormError />
                    )}

                    <I18n
                        t={translate}
                        className={style.label}
                        tKey={`label.top${isForce ? '.force' : ''}`}
                    />

                    <OccupationField
                        isOther={isOther}
                        setIsOther={setIsOther}
                        classNames={{
                            control: style.control,
                            controlOther: style.controlOther,
                            suggestionsWrapper: style.suggestionsWrapper
                        }}
                    />

                    <div className={style.controls}>
                        <Button
                            type="submit"
                            className={style.button}
                            disabled={hasValidationErrors || submitting}
                        >
                            {translate('button.submit')}
                        </Button>

                        <Button
                            view="link"
                            onClick={handleCloseClick}
                            className={style.button}
                            disabled={submitting}
                        >
                            {translate(`button.close${isForce ? '.force' : ''}`)}
                        </Button>
                    </div>
                </form>
            )}
        />
    );
};
